/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import { type ReactNode } from 'react';
import {
  type ExternalToast as SonnerExternalToast,
  toast as sonnerToast,
  Toaster as SonnerToaster,
  type ToasterProps as SonnerToasterProps,
  type ToastT as SonnerToastT,
} from 'sonner';

import { type CommonProps } from '../_common/types';
import { CleanIconButton } from '../clean-icon-button';
import {
  CheckCircleIconSolid,
  ExclamationCircleIconSolid,
  XMarkIconOutline,
} from '../icons';
import { OutlinedButton } from '../outlined-button';
import { ProgressBar } from '../progress-bar';

type ToastVariants = 'neutral' | 'success' | 'danger' | 'progress-bar';

type ToastCommandVariants = 'neutral' | 'success' | 'danger' | 'progressBar';

type ToastId = string | number;

type BaseToastProps = CommonProps<
  'div',
  {
    /** If `true` a close button will be displayed in the toast. */
    isCloseable?: boolean;
    /** If `true` the toast will close when the action button is triggered. */
    shouldCloseOnAction?: boolean;
    /** Function that will be called when the toast is closed. */
    onClose?: (id: ToastId) => void;
    /** If true the toast will close automatically after a certain time. */
    shouldAutoClose?: boolean;
    /** Function that will be called when the toast is automatically closed. */
    onAutoClose?: (id: ToastId) => void;
    /** If set an action button will be displayed. The value here will be displayed
     * in the action button. If this is set the toast property `shouldAutoClose` will
     * automatically be set to `false` unless overridden. */
    actionLabel?: string;
    /** Function that will be called when the action button is clicked. */
    onAction?: (id: ToastId) => void;
    /** If a dev wishes to set the underlying framework props (Sonner Toast).
     *  https://sonner.emilkowal.ski/toast#api-reference */
    sonnerToastProps?: SonnerExternalToast;
    /** If true the toast is controlled by the parent. */
    isControlled?: boolean;
  }
>;

interface ToastNeutralProps extends BaseToastProps {
  icon?: ReactNode;
}
// oxlint-disable-next-line @typescript-eslint/no-empty-object-type
interface ToastSuccessProps extends BaseToastProps {}
// oxlint-disable-next-line @typescript-eslint/no-empty-object-type
interface ToastDangerProps extends BaseToastProps {}

interface ToastProgressBarProps extends BaseToastProps {
  icon?: ReactNode;
  initialProgressBarValue?: number;
}

type ToastVariantsProps =
  | ToastNeutralProps
  | ToastSuccessProps
  | ToastDangerProps
  | ToastProgressBarProps;

interface ToasterProps extends Pick<
  SonnerToasterProps,
  'position' | 'visibleToasts' | 'className'
> {
  sonnerToasterProps?: SonnerToasterProps;
}

const Toaster = (props: ToasterProps) => {
  const {
    position = 'bottom-left',
    visibleToasts = 1,
    className,
    sonnerToasterProps,
  } = props;

  const classes = classNames('ndl-toast-container', className);

  return (
    <SonnerToaster
      className={classes}
      position={position}
      visibleToasts={visibleToasts}
      {...sonnerToasterProps}
    />
  );
};

type SonnerToast = typeof sonnerToast;
interface CustomSonnerToast extends SonnerToast {
  needle: Record<string, { onClose?: (id: ToastId) => void }>;
}

function onCloseCallback(
  callback: ((id: ToastId) => void) | undefined,
  id: ToastId,
) {
  if (callback) {
    callback(id);
    // Cleanup
    delete (sonnerToast as CustomSonnerToast).needle[id];
  }
}

type ToastElement = ToastVariantsProps & {
  icon: ReactNode;
  id: ToastId;
  variant: ToastVariants;
  text: ReactNode;
  initialProgressBarValue?: number;
  isControlled?: boolean;
};

const Toast = ({
  icon,
  id,
  text,
  variant,
  isCloseable,
  onClose,
  actionLabel,
  onAction,
  shouldCloseOnAction,
  initialProgressBarValue,
  isControlled,
  htmlAttributes,
  ref,
  ...restProps
}: ToastElement) => {
  const isProgressBar = variant === 'progress-bar';

  const handleOnClose = () => {
    if (isControlled === true) {
      onCloseCallback(onClose, id);
      return;
    }
    sonnerToast.dismiss(id);
    onCloseCallback(onClose, id);
  };

  const handleOnAction = () => {
    if (onAction) {
      onAction(id);
    }
    if (shouldCloseOnAction === true) {
      sonnerToast.dismiss(id);
      onCloseCallback(onClose, id);
    }
  };

  return (
    <div
      id={String(id)}
      className="ndl-toast"
      tabIndex={-1}
      ref={ref}
      data-variant={variant}
      data-testid="ndl-toast"
      {...restProps}
      {...htmlAttributes}
    >
      <div className="ndl-toast-content" tabIndex={-1}>
        {icon && (
          <div className={`ndl-toast-icon ndl-toast-${variant}`}>{icon}</div>
        )}
        <div className="ndl-toast-text">{text}</div>
        {actionLabel && (
          <OutlinedButton
            className="ndl-toast-action"
            size="small"
            onClick={handleOnAction}
          >
            {actionLabel}
          </OutlinedButton>
        )}
        {(isCloseable || actionLabel || isProgressBar) && (
          <CleanIconButton
            className="ndl-toast-close-button"
            onClick={handleOnClose}
            description={null}
            size="small"
            htmlAttributes={{
              'aria-label': 'Close',
            }}
          >
            <XMarkIconOutline />
          </CleanIconButton>
        )}
      </div>
      {isProgressBar && (
        <ProgressBar size="small" value={initialProgressBarValue ?? 0} />
      )}
    </div>
  );
};

function getProps(
  onClose: ((id: ToastId) => void) | undefined,
  shouldAutoClose: boolean | undefined,
  onAutoClose: ((id: ToastId) => void) | undefined,
  onAction: ((id: ToastId) => void) | undefined,
  sonnerToastProps: SonnerExternalToast | undefined,
  variant: ToastVariants,
) {
  const isProgressBar = variant === 'progress-bar';
  // https://sonner.emilkowal.ski/toast#api-reference
  return {
    duration:
      !isProgressBar && shouldAutoClose ? (!onAction ? 5000 : 8000) : Infinity,
    unstyled: true,
    onDismiss: (toast: SonnerToastT) => {
      onCloseCallback(onClose, toast.id);
    },
    onAutoClose: (toast: SonnerToastT) => {
      if (onAutoClose) {
        return onAutoClose(toast.id);
      }
    },
    ...sonnerToastProps,
  };
}

function createToast(
  text: ReactNode,
  props: ToastVariantsProps,
  variant: ToastVariants,
  icon: ReactNode,
) {
  const {
    ref,
    sonnerToastProps = {},
    onClose,
    shouldAutoClose,
    onAutoClose,
    onAction,
    ...restProps
  } = props;

  const { id: sonnerToastPropsId } = sonnerToastProps;
  sonnerToastProps.id = sonnerToastPropsId ?? `ndl-toast-${Date.now()}`;

  // Unfortunately calling sonnerToast.dismiss does not trigger the onDismiss
  // callback (weird). So we need to save the onClose callback and call it manually
  // when calling dismiss manually as we want an onClose callback.
  // https://sonner.emilkowal.ski/toast#on-close-callback
  // https://github.com/emilkowalski/sonner/blob/main/src/index.tsx#L320
  // https://github.com/emilkowalski/sonner/blob/main/src/index.tsx#L359
  (sonnerToast as CustomSonnerToast).needle = {
    [String(sonnerToastProps.id)]: {
      onClose,
    },
  };

  return sonnerToast.custom(
    (id: ToastId) => (
      <Toast
        {...restProps}
        ref={ref}
        id={id}
        text={text}
        variant={variant}
        icon={icon}
        onClose={onClose}
        onAction={onAction}
      />
    ),
    {
      ...getProps(
        onClose,
        shouldAutoClose,
        onAutoClose,
        onAction,
        sonnerToastProps,
        variant,
      ),
    },
  );
}

// eslint-disable-next-line no-redeclare
interface Toast {
  neutral: (text: ReactNode, props?: ToastNeutralProps) => ToastId;
  success: (text: ReactNode, props?: ToastSuccessProps) => ToastId;
  danger: (text: ReactNode, props?: ToastDangerProps) => ToastId;
  progressBar: (text: ReactNode, props?: ToastProgressBarProps) => ToastId;
  updateProgressBarValue: (id: ToastId, value: number) => void;
  close: (id: ToastId) => void;
  sonnerToast: typeof sonnerToast;
}

const toast: Toast = {
  close: (id) => {
    sonnerToast.dismiss(id);
    onCloseCallback((sonnerToast as CustomSonnerToast).needle[id]?.onClose, id);
  },
  danger: (text: ReactNode, props = {}) =>
    createToast(text, props, 'danger', <ExclamationCircleIconSolid />),
  neutral: (text, props = {}) => {
    const { icon, ...restProps } = props;
    return createToast(text, restProps, 'neutral', icon);
  },
  progressBar: (text: ReactNode, props = {}) => {
    const { icon, ...restProps } = props;
    return createToast(text, restProps, 'progress-bar', icon);
  },
  sonnerToast,
  success: (text: ReactNode, props = {}) => {
    const icon = <CheckCircleIconSolid />;
    return createToast(text, props, 'success', icon);
  },
  updateProgressBarValue: (id, givenValue) => {
    const value = givenValue > 100 ? 100 : givenValue < 0 ? 0 : givenValue;

    // Unable to use the sonner toast.update on custom toasts.
    const widthValue = `${value}%`;
    const toastElement = document.getElementById(String(id)) as HTMLDivElement;

    if (!toastElement) {
      return;
    }

    const progressBarContainerElement = toastElement.querySelector(
      '.ndl-progress-bar-container',
    ) as HTMLDivElement;
    progressBarContainerElement.ariaValueNow = String(value);
    const progressBarElement = progressBarContainerElement.querySelector(
      '.ndl-progress-bar',
    ) as HTMLDivElement;

    if (!progressBarContainerElement) {
      return;
    }

    progressBarElement.style.setProperty('width', widthValue);
  }, // If devs wish to directly access sonner toast.
};

export { Toaster, toast };
export type {
  ToasterProps,
  ToastVariants,
  ToastCommandVariants,
  ToastNeutralProps,
  ToastSuccessProps,
  ToastDangerProps,
  ToastProgressBarProps,
  ToastVariantsProps,
  ToastElement,
  ToastId,
};
